/**
 * Provides an administrative interface for configuring the Financials
 * (Impact Targets) dashboard widget. Allows administrators to manage
 * the Power BI reporting endpoint used to display financial and impact-
 * related analytics, ensuring the widget is connected to the correct
 * data source. Supports loading existing widget configurations, updating
 * integration settings, tracking unsaved changes, and persisting
 * configuration updates for the Financials dashboard experience.
 */
import React, { useEffect, useState } from "react";
import {
  getWidgetConfigurations,
  updateWidgetConfigurations,
} from "@/services/widgetConfig";

const FEATURE_CODE = "dashboard.impacttargets";

const FinancialsConfig: React.FC = () => {
  const [savedConfig, setSavedConfig] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const [financialsPowerBiUrl, setFinancialsPowerBiUrl] = useState("");

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    const config = await getWidgetConfigurations();

    const financialsConfig = config?.[FEATURE_CODE] || {};

    setSavedConfig(financialsConfig);

    setFinancialsPowerBiUrl(
      financialsConfig.PowerBIFinancialsUrl || "",
    );
  };

  const hasChanges =
    !!savedConfig &&
    financialsPowerBiUrl !==
      (savedConfig.PowerBIFinancialsUrl || "");

  const handleSave = async () => {
    setSaving(true);

    try {
      const payload = {
        featureCode: FEATURE_CODE,
        configs: {
          PowerBIFinancialsUrl: financialsPowerBiUrl,
        },
      };

      await updateWidgetConfigurations(payload);

      setSavedConfig(payload.configs);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!savedConfig) return;

    setFinancialsPowerBiUrl(
      savedConfig.PowerBIFinancialsUrl || "",
    );
  };

  return (
    <div className="border border-gray-200 rounded-md bg-[#fbfaf7] p-3 space-y-3">
      <div className="flex items-center gap-4 text-xs">
        <span className="w-44 text-gray-700">
          Financials Power BI URL
        </span>

        <input
          type="text"
          value={financialsPowerBiUrl}
          onChange={(e) => setFinancialsPowerBiUrl(e.target.value)}
          className="flex-1 border border-gray-300 rounded px-3 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          placeholder="Enter Power BI URL"
        />
      </div>

      <div className="flex justify-end gap-2 items-center pt-2">
        {hasChanges && (
          <div className="text-orange-600 border border-orange-300 bg-orange-50 px-2 py-0.5 rounded text-xs">
            Unsaved
          </div>
        )}

        <button
          className="bg-[#e7e5e0] px-3 py-1 text-xs"
          onClick={handleCancel}
          disabled={!hasChanges}
        >
          Cancel
        </button>

        <button
          className="bg-[var(--color-brand)] text-white px-3 py-1 text-xs"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default FinancialsConfig;